import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Layout from '@/components/feature/Layout';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ArticleRow from '@/components/feature/ArticleRow';
import { getCategoryById } from '@/mocks/news';
import { getById, getRelated } from '@/utils/news';
import { formatDateTime, formatViews } from '@/utils/format';
import { useFavorites } from '@/hooks/useFavorites';
import { useSEO } from '@/hooks/useSEO';

export default function DetailPage() {
  const { newsId = '' } = useParams();
  const article = getById(newsId);
  const category = article ? getCategoryById(article.category) : undefined;
  const related = article ? getRelated(article, 4) : [];
  const { isFavorite, toggleFavorite } = useFavorites();
  const [toast, setToast] = useState('');

  useSEO({
    title: article ? `${article.title} - 911爆料网` : '新闻详情 - 911爆料网',
    description: article ? article.summary : '911爆料网新闻详情页。',
    keywords: article ? article.tags.join(',') : '911爆料网,新闻详情',
  });

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, [newsId]);

  const showToast = useCallback((msg: string) => {
    setToast(msg);
    window.setTimeout(() => setToast(''), 2000);
  }, []);

  const handleCopy = useCallback(async () => {
    const url = window.location.href;
    try {
      await navigator.clipboard.writeText(url);
      showToast('链接已复制，可粘贴分享');
    } catch {
      showToast('复制失败，请手动复制地址栏链接');
    }
  }, [showToast]);

  if (!article) {
    return (
      <Layout>
        <div className="mx-auto flex max-w-[1280px] flex-col items-center justify-center px-4 py-24 text-center md:px-6">
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-secondary-100 text-secondary-600">
            <i className="ri-file-warning-line text-3xl"></i>
          </span>
          <h1 className="mt-5 font-heading text-2xl font-bold text-foreground-950">
            这条爆料找不到了
          </h1>
          <p className="mt-2 max-w-md text-sm text-foreground-600">
            内容可能已被移除或链接有误，你可以返回首页浏览最新的爆料资讯。
          </p>
          <Link
            to="/"
            className="mt-6 inline-flex items-center gap-1.5 rounded-md bg-primary-600 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-500"
          >
            <i className="ri-home-4-line"></i>
            返回首页
          </Link>
        </div>
      </Layout>
    );
  }

  const favorited = isFavorite(article.id);

  return (
    <Layout>
      <div className="mx-auto max-w-[1280px] px-4 py-6 md:px-6 md:py-8">
        <Breadcrumb
          items={[
            { label: '首页', to: '/' },
            { label: category?.name ?? '新闻', to: `/category/${article.category}` },
            { label: '正文' },
          ]}
        />

        <div className="mt-5 grid grid-cols-1 gap-8 lg:grid-cols-3">
          <article className="lg:col-span-2">
            <div className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-7">
              <div className="flex flex-wrap items-center gap-2 text-xs">
                <Link
                  to={`/category/${article.category}`}
                  className="rounded-md bg-primary-600 px-2.5 py-1 font-medium text-background-50 transition-colors hover:bg-primary-500"
                >
                  {category?.name}
                </Link>
                {article.hot && (
                  <span className="flex items-center gap-1 rounded-md bg-secondary-500 px-2.5 py-1 font-semibold text-background-50">
                    <i className="ri-fire-fill"></i>
                    热点
                  </span>
                )}
                <span className="text-foreground-500">
                  {formatDateTime(article.publishedAt)}
                </span>
              </div>

              <h1 className="mt-4 font-heading text-2xl font-extrabold leading-snug text-foreground-950 md:text-3xl">
                {article.title}
              </h1>

              <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 border-y border-background-200 py-3 text-sm text-foreground-600">
                <span className="flex items-center gap-1.5">
                  <i className="ri-user-line text-primary-600"></i>
                  {article.author}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-newspaper-line text-primary-600"></i>
                  来源：{article.source}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-eye-line text-primary-600"></i>
                  {formatViews(article.views)} 阅读
                </span>
              </div>

              <div className="mt-5 overflow-hidden rounded-lg">
                <img
                  src={article.cover}
                  alt={article.title}
                  title={`${article.title} - 911爆料网`}
                  className="h-auto max-h-[420px] w-full object-top"
                />
              </div>

              <div className="mt-6 space-y-4 text-[15px] leading-8 text-foreground-800 md:text-base">
                {article.content.map((para, index) => (
                  <p key={index}>{para}</p>
                ))}
              </div>

              {article.quote && (
                <blockquote className="mt-6 rounded-lg border-l-4 border-accent-500 bg-accent-50 p-4 font-heading text-base font-medium italic text-foreground-800">
                  {article.quote}
                </blockquote>
              )}

              <div className="mt-6 flex flex-wrap gap-2">
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="rounded-md bg-background-100 px-2.5 py-1 text-xs text-foreground-600"
                  >
                    #{tag}
                  </span>
                ))}
              </div>

              <div className="mt-7 flex flex-wrap items-center justify-between gap-4 border-t border-background-200 pt-5">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-foreground-500">分享到</span>
                  <button
                    type="button"
                    onClick={() => showToast('请复制链接后分享到微信')}
                    className="flex h-9 w-9 items-center justify-center rounded-full bg-background-100 text-foreground-600 transition-colors hover:bg-primary-100 hover:text-primary-700"
                    aria-label="分享到微信"
                  >
                    <i className="ri-wechat-line text-lg"></i>
                  </button>
                  <a
                    href={`https://bucns.cn/share/share.php?title=${encodeURIComponent(
                      article.title,
                    )}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex h-9 w-9 items-center justify-center rounded-full bg-background-100 text-foreground-600 transition-colors hover:bg-primary-100 hover:text-primary-700"
                    aria-label="分享到微博"
                  >
                    <i className="ri-weibo-line text-lg"></i>
                  </a>
                  <button
                    type="button"
                    onClick={handleCopy}
                    className="flex h-9 w-9 items-center justify-center rounded-full bg-background-100 text-foreground-600 transition-colors hover:bg-primary-100 hover:text-primary-700"
                    aria-label="复制链接"
                  >
                    <i className="ri-link"></i>
                  </button>
                </div>

                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      const nowFav = toggleFavorite(article.id);
                      showToast(nowFav ? '已收藏到本地' : '已取消收藏');
                    }}
                    className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-md px-4 py-2 text-sm font-semibold transition-colors ${
                      favorited
                        ? 'bg-accent-500 text-primary-950 hover:bg-accent-400'
                        : 'border border-background-300 bg-background-50 text-foreground-700 hover:border-primary-400 hover:text-primary-600'
                    }`}
                  >
                    <i className={favorited ? 'ri-bookmark-fill' : 'ri-bookmark-line'}></i>
                    {favorited ? '已收藏' : '收藏爆料'}
                  </button>
                  <Link
                    to={`/category/${article.category}`}
                    className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2 text-sm font-semibold text-foreground-700 transition-colors hover:border-primary-400 hover:text-primary-600"
                  >
                    <i className="ri-arrow-left-line"></i>
                    返回栏目
                  </Link>
                </div>
              </div>
            </div>
          </article>

          <aside className="flex flex-col gap-6">
            <section className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
              <h2 className="mb-3 flex items-center gap-2 border-b-2 border-primary-100 pb-3 font-heading text-lg font-bold text-foreground-950">
                <i className="ri-links-line text-primary-600"></i>
                相关爆料
              </h2>
              <div className="flex flex-col">
                {related.map((item) => (
                  <ArticleRow key={item.id} article={item} />
                ))}
              </div>
            </section>

            <section className="rounded-lg bg-primary-900 p-5 text-background-100">
              <h2 className="font-heading text-lg font-bold text-background-50">
                你有更猛的料？
              </h2>
              <p className="mt-2 text-sm leading-relaxed text-background-300">
                你的每一条线索都值得被认真对待，欢迎随时向我们爆料。
              </p>
              <Link
                to="/submit"
                className="mt-4 inline-flex items-center gap-1.5 rounded-md bg-accent-500 px-4 py-2 text-sm font-semibold text-primary-950 transition-colors hover:bg-accent-400"
              >
                <i className="ri-send-plane-line"></i>
                去爆料
              </Link>
            </section>
          </aside>
        </div>
      </div>

      {toast && (
        <div className="fixed bottom-8 left-1/2 z-50 -translate-x-1/2 animate-fade-up rounded-full bg-foreground-950/90 px-5 py-2.5 text-sm text-background-50">
          {toast}
        </div>
      )}
    </Layout>
  );
}